import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useApolloClient } from '@apollo/client';
import { Button, Dialog, Input, Toast } from 'antd-mobile';
import { Header } from '../components/Header';
import { t } from '../../../i18n';
import { TmsPageState } from '../types/tms';
import { TmsWaypointStepIndicator } from './components/TmsWaypointStepIndicator';
import { TmsContainerFormPopup } from './components/TmsContainerFormPopup';
import { TmsCountedInput } from './components/TmsCountedInput';
import MultiPhotoGalleryView from '../components/MultiPhotoGalleryView';
import { getBottomNavBarContainerStyle } from '../../../utils/deviceUtils';
import { PdaPath } from '../../../enums/PdaNavigatePathEnum';
import {
    TmsTaskContainerType,
    TMS_CONTAINER_TYPE_ORDER,
    TmsOperationSourceEnum,
    TmsTaskWaypointNodeType,
    WaypointType,
    TmsRouteBusinessType,
    TmsTaskClassificationType,
} from '../../../enums/TmsEnum';
import { pdaOpenCamera, startPrepareCameraLocation, FirstPhotoWatermarkLocation } from '../../../utils/appRuntime';
import { useSaveTmsTaskWaypointLocationInfo } from './hooks/useSaveTmsTaskWaypointLocationInfo';
import { getCurrentServerTimeMillis } from '../../../utils/pdaCameraUtils';
import { GET_ACCOUNT_BIND_WAREHOUSE, GetAccountBindWarehouseResponse } from '../../../graphql/queries/warehouseQueries';
import {
    GET_TMS_LOADING_DETAIL_SUMMARY,
    GetTmsLoadingDetailSummaryResponse,
    SAVE_TMS_LOADING_DETAIL_INFO,
    GET_TMS_TASK_WAYPOINT_LOADING_INFO,
    GetTmsTaskWaypointLoadingInfoResponse,
    GET_NEEDS_TMS_UNLOADING_OPERATION,
    NeedsTmsUnloadingOperationResponse,
} from '../../../graphql/queries/tmsTaskWarehouseOperationQueries';
import { useEffect, useRef, useState } from 'react';
import { EditOutlined } from '@ant-design/icons';
import { fileUpload } from '../../../utils/storageUtils';
import { base64ToFile } from '../../../services/DeliveryDataService';

export default function TmsLoadingDetailPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const { backRoute, ...taskData } = (location.state as TmsPageState) || {};

    // 必须在早期返回之前调用所有 Hooks
    const taskId = taskData?.taskId ? parseInt(taskData.taskId) : NaN;
    const taskWayPointId = taskData?.taskWaypointId ? parseInt(taskData.taskWaypointId) : NaN;
    const isValidTaskId = !isNaN(taskId) && !isNaN(taskWayPointId);
    const hasUnLoadingTime = taskData?.task?.hasUnLoadingTime === true;
    const isOriginStation = taskData?.waypointType === WaypointType.ORIGIN;
    const shouldCheckUnloading = isValidTaskId && !hasUnLoadingTime && !isOriginStation;

    const { data: unloadingCheckData } = useQuery<NeedsTmsUnloadingOperationResponse>(
        GET_NEEDS_TMS_UNLOADING_OPERATION,
        {
            variables: { taskId, taskWayPointId },
            skip: !shouldCheckUnloading
        }
    );

    useEffect(() => {
        if (!shouldCheckUnloading) return;
        if (unloadingCheckData?.needsTmsUnloadingOperation === true) {
            Dialog.alert({
                content: t('pda.pages.sealed.unloadingRequired'),
                confirmText: t('pda.common.confirm') || '确认',
                onConfirm: () => {
                    if (backRoute) navigate(backRoute);
                    else navigate(-1);
                },
            });
        }
    }, [shouldCheckUnloading, unloadingCheckData, backRoute, navigate]);

    // 调用后端API获取装载信息汇总
    const { data, loading, error } = useQuery<GetTmsLoadingDetailSummaryResponse>(
        GET_TMS_LOADING_DETAIL_SUMMARY,
        {
            variables: {
                taskId,
                taskWayPointId
            },
            skip: !isValidTaskId
        }
    );

    const apolloClient = useApolloClient();
    const { data: warehouseData } = useQuery<GetAccountBindWarehouseResponse>(GET_ACCOUNT_BIND_WAREHOUSE);
    // 查询当前途经点已保存的装载备注和车内照片（用于页面初始化回显）
    const { data: waypointLoadingInfoData } = useQuery<GetTmsTaskWaypointLoadingInfoResponse>(
        GET_TMS_TASK_WAYPOINT_LOADING_INFO,
        {
            variables: {
                id: isValidTaskId ? taskWayPointId.toString() : ''
            },
            skip: !isValidTaskId
        }
    );

    const [saveLoadingDetailInfo, { loading: saveLoadingDetailInfoLoading }] = useMutation<{
        saveTmsLoadingDetailInfo: boolean;
    }>(SAVE_TMS_LOADING_DETAIL_INFO);

    const [loadingInfoRemark, setLoadingInfoRemark] = useState('');
    // 用于展示的照片（URL 或 base64）
    const [loadingInfoInteriorPhotos, setLoadingInfoInteriorPhotos] = useState<string[]>([]);
    // 已存在于服务端的车内照片 S3 key 列表
    const [loadingInfoInteriorPhotoKeysFromServer, setLoadingInfoInteriorPhotoKeysFromServer] = useState<string[]>([]);
    const [firstPhotoLocation, setFirstPhotoLocation] = useState<FirstPhotoWatermarkLocation | null>(null);
    const { save: saveTmsTaskWaypointLocationInfo } = useSaveTmsTaskWaypointLocationInfo();

    // 初始化备注和车内照片（如果后端已有保存记录）
    useEffect(() => {
        const waypoint = waypointLoadingInfoData?.getTmsTaskWaypointById;
        if (!waypoint) {
            return;
        }

        if (typeof waypoint.loadingInfoRemark === 'string') {
            setLoadingInfoRemark(waypoint.loadingInfoRemark);
        }

        // 初始化已有的 S3 key 和用于展示的 URL
        if (Array.isArray(waypoint.loadingInfoInteriorPhotosJson)) {
            setLoadingInfoInteriorPhotoKeysFromServer(waypoint.loadingInfoInteriorPhotosJson);
        } else {
            setLoadingInfoInteriorPhotoKeysFromServer([]);
        }

        if (
            Array.isArray(waypoint.loadingInfoInteriorPhotosUrlsJson) &&
            waypoint.loadingInfoInteriorPhotosUrlsJson.length > 0
        ) {
            setLoadingInfoInteriorPhotos(waypoint.loadingInfoInteriorPhotosUrlsJson);
        } else {
            setLoadingInfoInteriorPhotos([]);
        }
    }, [waypointLoadingInfoData]);

    // 处理API错误
    useEffect(() => {
        if (error) {
            console.error('Failed to fetch loading detail summary:', error);
            Toast.show({
                content: t('pda.common.loadFailed') || 'Failed to load data',
                position: 'center',
                duration: 2000
            });
        }
    }, [error]);

    // 定义类型
    interface LocalContainerInfo {
        containerType: number;
        containerCount: number;
    }

    // 所有 Hooks 必须在条件判断之前调用
    const [containerPopupVisible, setContainerPopupVisible] = useState(false);
    const [activeGroupIndex, setActiveGroupIndex] = useState<number | null>(null);
    const [localContainers, setLocalContainers] = useState<Record<number, LocalContainerInfo[]>>({});
    const [containerForm, setContainerForm] = useState<Record<number, string>>(
        Object.fromEntries(TMS_CONTAINER_TYPE_ORDER.map(t => [t, '']))
    );
    const [containerPopupMode, setContainerPopupMode] = useState<'add' | 'edit'>('add');
    const hasModifiedRef = useRef(false);

    useEffect(() => {
        startPrepareCameraLocation();
    }, []);

    // 重新进入页面时清空保存状态，避免多次进入互相影响
    useEffect(() => {
        hasModifiedRef.current = false;
        setLocalContainers({});
        setContainerPopupVisible(false);
        setActiveGroupIndex(null);
    }, [taskId, taskWayPointId]);

    // 早期返回必须在所有 Hooks 调用之后
    if (!taskData) {
        return null;
    }

    if (!isValidTaskId) {
        return null;
    }

    const summary = data?.getTmsLoadingDetailSummary;

    const getContainerTypeName = (type?: number | null): string => {
        switch (type) {
            case TmsTaskContainerType.CARTON:
                return t('pda.pages.loadingDetail.containerTypeCardboardBox') || '卡板箱';
            case TmsTaskContainerType.BAG:
                return t('pda.pages.loadingDetail.containerTypeSnakeSkinBag') || '蛇皮袋';
            case TmsTaskContainerType.PALLET:
                return t('pda.pages.loadingDetail.containerTypePallet') || '托盘';
            case TmsTaskContainerType.GAYLORD:
                return t('pda.pages.loadingDetail.containerTypeGaylord') || 'Gaylord';
            default:
                return '-';
        }
    };

    const getDisplayedContainers = (
        group: {
            bagQuantity: number | string;
            containers?: { containerType: number | string; containerCount?: number | string }[]
        },
        index: number
    ): { containerType: number; containerCount: number }[] => {
        if (localContainers[index]?.length > 0) {
            return localContainers[index];
        }
        const serverContainers = (group.containers ?? [])
            .map(c => ({
                containerType: Number(c.containerType),
                containerCount: Number(c.containerCount ?? 0),
            }))
            .filter(c => Number.isFinite(c.containerType) && Number.isFinite(c.containerCount) && c.containerCount > 0);
        if (serverContainers.length > 0) {
            return serverContainers;
        }
        return [];
    };

    const handleOpenContainerPopup = (
        groupIndex: number,
        existingContainers?: { containerType: number; containerCount: number }[]
    ) => {
        setActiveGroupIndex(groupIndex);
        if (existingContainers && existingContainers.length > 0) {
            setContainerPopupMode('edit');
            setContainerForm(
                Object.fromEntries(
                    TMS_CONTAINER_TYPE_ORDER.map(t => [
                        t,
                        existingContainers.find(item => item.containerType === t)?.containerCount.toString() ?? '0'
                    ])
                )
            );
        } else {
            setContainerPopupMode('add');
            setContainerForm(Object.fromEntries(TMS_CONTAINER_TYPE_ORDER.map(t => [t, ''])));
        }
        setContainerPopupVisible(true);
    };

    const handleConfirmContainerPopup = () => {
        if (activeGroupIndex === null) {
            setContainerPopupVisible(false);
            return;
        }

        const types = TMS_CONTAINER_TYPE_ORDER;
        const newContainers: LocalContainerInfo[] = [];
        let totalCount = 0;

        for (const type of types) {
            const value = containerForm[type];
            if (value === '' || value == null) continue;

            const count = Number(value);
            if (Number.isNaN(count) || count < 0) {
                Toast.show({
                    content: t('pda.pages.loadingDetail.containerCountNonNegative') || '填写的容器数量必须大于等于0',
                    position: 'center',
                    duration: 2000
                });
                return;
            }
            if (count > 0) {
                newContainers.push({ containerType: type, containerCount: count });
                totalCount += count;
            }
        }

        if (totalCount === 0) {
            Toast.show({
                content: t('pda.pages.loadingDetail.containerCountMustBeGreaterThanZero') || '容器总数必须大于0',
                position: 'center',
                duration: 2000
            });
            return;
        }

        hasModifiedRef.current = true;
        setLocalContainers(prev => ({
            ...prev,
            [activeGroupIndex]: newContainers
        }));
        setContainerPopupVisible(false);
    };
    
    // 是否有装车卡片（有目的地分组时显示备注和照片区域）
    const hasLoadingCards = (summary?.destinationGroups?.length ?? 0) > 0;

    // 校验是否所有目的地都有填写容器信息
    const hasUnfilledContainers =
        summary?.destinationGroups.some((group, index) => {
            const displayed = getDisplayedContainers(
                group as { bagQuantity: number; containers?: { containerType: number; containerCount?: number }[] },
                index
            );
            return displayed.length === 0;
        }) ?? false;

    const handleNextStepClick = async () => {
        if (hasUnfilledContainers) {
            Toast.show({
                content: t('pda.pages.loadingDetail.pleaseEnterLoadingContainerInfo') || '请填写装车容器信息',
                position: 'center',
                duration: 2000
            });
            return;
        }
        if (hasLoadingCards) {
            const photoCount = loadingInfoInteriorPhotos.length;
            if (photoCount < 1 || photoCount > 3) {
                Toast.show({
                    content: t('pda.pages.loadingDetail.interiorPhotosRequired') || '请上传1～3张车内照片',
                    position: 'center',
                    duration: 2000
                });
                return;
            }
        }
        try {
            const summaryData = data?.getTmsLoadingDetailSummary;
            if (!summaryData?.destinationGroups?.length) {
                navigate(PdaPath.PDA_TMS_SEALING, { state: { backRoute, ...taskData } });
                return;
            }

            if (!hasModifiedRef.current) {
                navigate(PdaPath.PDA_TMS_SEALING, { state: { backRoute, ...taskData } });
                return;
            }

            const items: { sortingCode: string | null; containerType: number | null; containerCount: number }[] = [];
            summaryData.destinationGroups.forEach((group, index) => {
                const displayed = getDisplayedContainers(
                    group as { bagQuantity: number; containers?: { containerType: number; containerCount?: number }[] },
                    index
                );
                displayed.forEach(c => {
                    const containerType = Number(c.containerType ?? 0);
                    const containerCount = Number(c.containerCount ?? 0);
                    items.push({
                        sortingCode: group.destinationSortingCode ?? null,
                        containerType,
                        containerCount
                    });
                });
            });

            // 车内照片：前 N 项为服务端已有（N = loadingInfoInteriorPhotoKeysFromServer.length），后续为本次新增 base64
            let loadingPhotoKeys: string[] = [...loadingInfoInteriorPhotoKeysFromServer];
            const newBase64Photos = loadingInfoInteriorPhotos.slice(loadingInfoInteriorPhotoKeysFromServer.length);

            if (hasLoadingCards && newBase64Photos.length > 0) {
                const newKeys = await Promise.all(
                    newBase64Photos.map(async (base64Photo, index) => {
                        const formattedBase64 = base64Photo.startsWith('data:')
                            ? base64Photo
                            : `data:image/jpeg;base64,${base64Photo}`;
                        const file = await base64ToFile(
                            formattedBase64,
                            `tms_loading_interior_${Date.now()}_${index}.jpg`
                        );
                        const result = await fileUpload(file, 'tms_loading_interior_photo');
                        return result.objectKey;
                    })
                );
                loadingPhotoKeys = [...loadingPhotoKeys, ...newKeys];
            }

            await saveTmsTaskWaypointLocationInfo(taskId, taskWayPointId, TmsTaskWaypointNodeType.LOADING, firstPhotoLocation);

            const input = {
                taskId: Number(taskId),
                taskWaypointId: Number(taskWayPointId),
                items,
                loadingInfoRemark: loadingInfoRemark?.trim() ?? '',
                loadingInfoInteriorPhotosJson: loadingPhotoKeys,
                operationSource: TmsOperationSourceEnum.PDA,
            };

            await saveLoadingDetailInfo({
                variables: { input }
            });

            navigate(PdaPath.PDA_TMS_SEALING, { state: { backRoute, ...taskData } });
        } catch (error: any) {
            // 在手机端看不到控制台，这里把后端返回的错误 message 直接展示出来
            // eslint-disable-next-line no-console
            console.error('saveTmsLoadingDetailInfo error:', error);

            const netErr = error?.networkError as { result?: { errors?: { message?: string }[] }; message?: string } | undefined;
            const graphQLErrorMessage =
                (error?.graphQLErrors && error.graphQLErrors[0]?.message) ||
                (netErr?.result?.errors?.[0]?.message);

            const message =
                graphQLErrorMessage ||
                netErr?.message ||
                error?.message ||
                t('pda.common.loadFailed') ||
                'Failed to load data';

            Toast.show({
                content: message,
                position: 'center',
                duration: 3000
            });
        }
    };

    // 获取所有目的地分拣码（用于显示"包裹目的中心"）
    const destinationSortingCodes = summary?.destinationGroups
        .map(group => group.destinationSortingCode)
        .filter((code): code is string => code != null)
        .join('、') || '';

    // 按容器类型汇总：每种类型一行，数量为该类型在各目的中心的合计（含本地编辑，保存后立即更新）
    const typeTotals: Record<number, number> = Object.fromEntries(TMS_CONTAINER_TYPE_ORDER.map(t => [t, 0]));
    summary?.destinationGroups?.forEach((group, index) => {
        const displayed = getDisplayedContainers(
            group as { bagQuantity: number; containers?: { containerType: number; containerCount?: number }[] },
            index
        );
        (displayed || []).forEach(
            (c: { containerType: number; containerCount?: number }) => {
                typeTotals[c.containerType] = (typeTotals[c.containerType] ?? 0) + (c.containerCount ?? 0);
            }
        );
    });
    const containerTypeRows = TMS_CONTAINER_TYPE_ORDER
        .filter(t => (typeTotals[t] ?? 0) > 0)
        .map(t => ({ typeName: getContainerTypeName(t), count: typeTotals[t] ?? 0 }));
    const totalContainerCount = containerTypeRows.reduce((sum, row) => sum + row.count, 0);
    const shouldShowPlannedLoadingQty =
        summary?.taskBusinessType === TmsRouteBusinessType.TIKTOK &&
        summary?.taskTaskType === TmsTaskClassificationType.THIRD_PARTY_TASK;
    const plannedLoadingQty = Number(summary?.totalShipmentPlanPlannedQty ?? 0);
    const isPlannedLoadingQtyMismatch = shouldShowPlannedLoadingQty && plannedLoadingQty !== totalContainerCount;

    const kMaxInteriorPhotoLength = 3;
    const kMinInteriorPhotoLength = 1;

    const handleLoadingInfoRemarkChange = (val: string) => {
        hasModifiedRef.current = true;
        setLoadingInfoRemark(val.slice(0, 50));
    };

    const handleInteriorPhotoBurstCapture = async () => {
        const currentCount = loadingInfoInteriorPhotos.length;
        const remaining = Math.max(0, kMaxInteriorPhotoLength - currentCount);
        const minSelection = currentCount >= kMinInteriorPhotoLength ? 0 : kMinInteriorPhotoLength - currentCount;
        const photoTitles = [
            t('pda.pages.tms.departure.firstPhoto') || '第一张照片',
            t('pda.pages.tms.departure.secondPhoto') || '第二张照片',
            t('pda.pages.tms.departure.thirdPhoto') || '第三张照片'
        ];
        const serverTimeMillis = await getCurrentServerTimeMillis(apolloClient);
        await pdaOpenCamera(
            remaining,
            minSelection,
            t('pda.pages.tms.departure.photoTips') || '',
            photoTitles,
            t('pda.pages.tms.departure.complete') || '',
            (newPhotos: string[], loc?: FirstPhotoWatermarkLocation | null) => {
                if (newPhotos?.length) {
                    hasModifiedRef.current = true;
                    setLoadingInfoInteriorPhotos(prev => [...prev, ...newPhotos].slice(0, kMaxInteriorPhotoLength));
                }
                if (loc != null) setFirstPhotoLocation(loc);
            },
            {
                enableWatermark: true,
                ianaTimeZone: warehouseData?.warehouse?.timezoneLocation,
                serverTimeMillis
            }
        );
    };

    const handleDeleteInteriorPhoto = (index: number) => {
        hasModifiedRef.current = true;
        setLoadingInfoInteriorPhotos(prev => prev.filter((_, i) => i !== index));
        setLoadingInfoInteriorPhotoKeysFromServer(prev => {
            if (index >= prev.length) return prev;
            return prev.filter((_, i) => i !== index);
        });
    };

    return (
        <div className="w-full h-screen flex flex-col gap-2 bg-gray-50">
            <div className="p-3 flex flex-col gap-4 flex-1 overflow-y-auto pb-20">
                <Header title={t('pda.pages.sealed.step2')} backRoute={backRoute} />
                <div className="flex flex-col gap-2">
                    {/* 步骤节点展示 */}
                    <TmsWaypointStepIndicator currentStep={2} />

                    {loading ? (
                        <div className="p-4 bg-white rounded-lg flex items-center justify-center">
                            <div>{t('pda.common.loading') || 'Loading...'}</div>
                        </div>
                    ) : summary ? (
                        <>
                            {/* 汇总信息 */}
                            <div className="p-4">
                                <div className="flex flex-col gap-3">
                                    {/* 包裹目的中心：和值在同一行，显示不下换行 */}
                                    <div className="flex flex-wrap gap-1 items-baseline">
                                        <span className="text-sm font-semibold text-black">
                                            {t('pda.pages.loadingDetail.parcelDestinationCenter') || '目的地中心：'}
                                        </span>
                                        <span className="text-base font-semibold text-purple-600">
                                            {destinationSortingCodes || '-'}
                                        </span>
                                    </div>
                                    {/* 总包牌数和总运单数在同一行，分别各占一半 */}
                                    <div className="flex gap-4">
                                        <div className="flex flex-wrap gap-1 items-baseline flex-1">
                                            <span className="text-sm font-semibold text-black">
                                                {t('pda.pages.loadingDetail.totalBagQuantity') || '总包牌数：'}
                                            </span>
                                            <span className="text-sm font-semibold text-purple-600">
                                                {summary.totalBagQuantity}
                                            </span>
                                        </div>
                                        <div className="flex flex-wrap gap-1 items-baseline flex-1">
                                            <span className="text-sm font-semibold text-black">
                                                {t('pda.pages.loadingDetail.totalWaybillQuantity') || '总运单数：'}
                                            </span>
                                            <span className="text-sm font-semibold text-purple-600">
                                                {summary.totalParcelQuantity}
                                            </span>
                                        </div>
                                    </div>
                                    {/* 总容器数 */}
                                    <div className="flex gap-4">
                                        <div className="flex flex-wrap gap-1 items-baseline flex-1">
                                            <span className="text-sm font-semibold text-black">
                                                {t('pda.pages.loadingDetail.containerCount') || '总容器数：'}
                                            </span>
                                            <span className={`text-sm font-semibold ${isPlannedLoadingQtyMismatch ? 'text-red-600' : 'text-purple-600'}`}>
                                                {totalContainerCount > 0 ? totalContainerCount : '-'}
                                            </span>
                                        </div>
                                        <div className="flex flex-wrap gap-1 items-baseline flex-1">
                                            {shouldShowPlannedLoadingQty && (
                                                <>
                                                    <span className="text-sm font-semibold text-black">
                                                        {t('pda.pages.loadingDetail.plannedLoadingQuantity') ||
                                                            '计划装载数量：'}
                                                    </span>
                                                    <span className="text-sm font-semibold text-purple-600">
                                                        {plannedLoadingQty}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 按目的地分组的信息卡片 */}
                            {summary.destinationGroups.length > 0 && (
                                <div className="flex flex-col gap-3">
                                    {summary.destinationGroups.map((group, index) => {
                                        const hasLocalContainers = localContainers[index] && localContainers[index].length > 0;
                                        const displayedContainers = getDisplayedContainers(
                                            group as { bagQuantity: number; containers?: { containerType: number; containerCount?: number }[] },
                                            index
                                        );

                                        return (
                                            <div
                                                key={index}
                                                className="bg-white rounded-lg px-4 py-1 shadow-[0_-2px_4px_-1px_rgba(0,0,0,0.08),0_2px_4px_-1px_rgba(0,0,0,0.08)]"
                                            >
                                                <div className="flex flex-col gap-3">
                                                    {/* 标题行：目的中心  */}
                                                    <div>
                                                        <span className={`bg-purple-500 text-white px-2 py-0.5 rounded text-xs font-medium inline-block`}>
                                                           {t('pda.pages.loadingDetail.destinationCenter')}:{group.destinationSortingCode || '-'}
                                                        </span>
                                                    </div>
                                                    {/* 标题行： 包牌/运单数量 和 容器信息 在同一行 */}
                                                    <div className="flex gap-4">
                                                        <div className="flex-1 text-center">
                                                            <span className="text-sm font-semibold text-black">
                                                                {t('pda.pages.loadingDetail.bagWaybillQuantity')}
                                                            </span>
                                                        </div>

                                                        <div className="flex-1 text-center">
                                                            <span className="text-sm font-semibold text-black">
                                                                {t('pda.pages.loadingDetail.containerQuantity')}
                                                            </span>
                                                        </div>

                                                    </div>
                                                    {/* 数量行：对应数量在同一行 */}
                                                    <div className="flex gap-4">
                                                        <div className="flex-1 text-center">
                                                            <span className="text-sm font-semibold text-black">
                                                                {group.bagQuantity}/{group.parcelQuantity}
                                                            </span>
                                                        </div>

                                                        <div className="flex-1 text-center flex flex-col items-center justify-center gap-1">
                                                            {displayedContainers && displayedContainers.length > 0 ? (
                                                                <div className="flex items-start gap-2 w-full justify-center">
                                                                    <div className="flex flex-col gap-1 flex-1 min-w-0">
                                                                        {displayedContainers.map(
                                                                            (
                                                                                container: { containerCount: number; containerType: number },
                                                                                idx: number
                                                                            ) => (
                                                                                <span
                                                                                    key={idx}
                                                                                    className="text-sm font-semibold text-purple-600"
                                                                                >
                                                                                    {`${container.containerCount}/${getContainerTypeName(container.containerType)}`}
                                                                                </span>
                                                                            )
                                                                        )}
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        className="flex-shrink-0 p-1 text-gray-500 hover:text-purple-600"
                                                                        onClick={() => handleOpenContainerPopup(index, displayedContainers)}
                                                                        aria-label={t('pda.pages.loadingDetail.editContainerInfo')}
                                                                    >
                                                                        <EditOutlined className="text-lg" />
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <button
                                                                    type="button"
                                                                    className="inline-flex items-center justify-center px-2 py-1 rounded border border-dashed border-red-400 bg-red-50 text-xs font-medium text-red-500"
                                                                    onClick={() => handleOpenContainerPopup(index)}
                                                                >
                                                                    {t('pda.pages.loadingDetail.pleaseEnterContainerInfo')}
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            {hasLoadingCards && (
                                <div className="p-4 bg-white rounded-lg shadow-[0_-2px_4px_-1px_rgba(0,0,0,0.08),0_2px_4px_-1px_rgba(0,0,0,0.08)] flex flex-col gap-4">
                                    <div className="text-base font-medium text-gray-700">
                                        {t('pda.pages.loadingDetail.remark')}
                                    </div>
                                    <TmsCountedInput
                                        value={loadingInfoRemark}
                                        onChange={handleLoadingInfoRemarkChange}
                                        placeholder={t('pda.pages.loadingDetail.remarkPlaceholder') as string}
                                    />
                                    <div className="text-base font-medium text-gray-700">
                                        {t('pda.pages.loadingDetail.uploadInteriorPhotosTitle')}
                                    </div>
                                    <MultiPhotoGalleryView
                                        photos={loadingInfoInteriorPhotos}
                                        onDelete={handleDeleteInteriorPhoto}
                                        onNativeBurstCapture={handleInteriorPhotoBurstCapture}
                                        maxDisplayCount={kMaxInteriorPhotoLength}
                                    />
                                </div>
                            )}
                        </>
                    ) : null}
                </div>
            </div>

            <TmsContainerFormPopup
                visible={containerPopupVisible}
                mode={containerPopupMode}
                containerForm={containerForm}
                onFormChange={setContainerForm}
                onConfirm={handleConfirmContainerPopup}
                onCancel={() => setContainerPopupVisible(false)}
                editTitle="pda.pages.loadingDetail.editContainerInfo"
                addTitle="pda.pages.loadingDetail.addContainerInfo"
                destinationCenter={
                    activeGroupIndex !== null
                        ? summary?.destinationGroups[activeGroupIndex]?.destinationSortingCode || '-'
                        : '-'
                }
            />

            {/* 固定在底部的按钮 */}
            <div {...getBottomNavBarContainerStyle('fixed bottom-0 left-0 w-full p-2 bg-white border-t border-gray-200 z-20')}>
                <div className="flex gap-2">
                    <Button
                        block
                        color="default"
                        onClick={() => navigate(PdaPath.PDA_TMS_LOADING, { state: { backRoute, ...taskData } })}
                        style={{ flex: 1 }}
                    >
                        {t('pda.common.previousStep')}
                    </Button>
                    <Button
                        block
                        color="primary"
                        loading={saveLoadingDetailInfoLoading}
                        onClick={handleNextStepClick}
                        style={{ flex: 1 }}
                    >
                        {t('pda.common.nextStep')}
                    </Button>
                </div>
            </div>
        </div>
    );
}
